<template>
<standard-dialog v-model="visible" scrollable persistent :width="1280">

  <template slot="title">
    <span class="title">{{ typeName }} {{ name }}</span>
  </template>

  <chart-line :data="data" :options="options"></chart-line>

</standard-dialog>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import Dialog from '@/components/utils/Dialog'
import colors from 'vuetify/es5/util/colors'
import chartLine from '@/components/charts/Line'

const colorNames = ['blue', 'red', 'green'];

export default {
  name: 'GatewayChart',
  mixins: [UserAuth],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    mac: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    }
  },
  components: {
    'standard-dialog': Dialog,
    'chart-line': chartLine,
  },
  data() {
    return {
      rawObject: {},
      missingData: []
    }
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    rawData() {
      let missing = [];
      let data = JSON.parse((this.rawObject || {}).result || '[]');
      let cleaned = data.reduce((acc, cur) => {
        let last = acc[acc.length - 1];
        if (last && cur[0] - last[0] > 12 * 60 * 1000) {
          acc.push([(cur[0] + last[0]) / 2, null, null, null]);
          missing.push({
            from: last[0],
            to: cur[0]
          });
        }
        acc.push(cur);
        return acc;
      }, []);
      this.missingData = missing;
      return cleaned;
    },
    data() {
      switch (this.type) {
        case 'load':
          return this.dataLoad;
        case 'memory':
          return this.dataMemory;
        case 'latency':
          return this.dataLatency;
      }
    },
    dataLoad() {
      return {
        datasets: [
          this.$t('gateway.one_minute'),
          this.$t('gateway.five_minutes'),
          this.$t('gateway.fifteen_minutes')
        ].map((title, i) => {
          return {
            data: (this.rawData || []).map((k, j) => ({
              t: k[0],
              y: k[i + 1]
            })),
            borderWidth: 1.5,
            lineTension: 0,
            pointRadius: 0.5,
            borderColor: colors[colorNames[i]].base,
            backgroundColor: colors[colorNames[i]].base,
            label: title,
            fill: false,
            hidden: i != 2
          };
        })
      };
    },
    dataMemory() {
      return {
        datasets: [{
          data: (this.rawData || []).map(k => ({
            t: k[0],
            y: k[2] ? k[2] / 1024 / 1024 : null
          })),
          borderWidth: 1.5,
          lineTension: 0,
          pointRadius: 0.5,
          borderColor: colors[colorNames[2]].base,
          backgroundColor: colors[colorNames[2]].base,
          label: this.$t('gateway.total_memory'),
          fill: false
        }, {
          data: (this.rawData || []).map(k => ({
            t: k[0],
            y: k[1] ? k[1] / 1024 / 1024 : null
          })),
          borderWidth: 1.5,
          lineTension: 0,
          pointRadius: 0.5,
          borderColor: colors[colorNames[0]].base,
          backgroundColor: colors[colorNames[0]].base,
          label: this.$t('gateway.free_memory'),
          fill: false
        }]
      }
    },
    dataLatency() {
      return {
        datasets: [{
          data: (this.rawData || []).map(k => ({
            t: k[0],
            y: k[1] ? Math.min(k[1], 1000) : k[1]
          })),
          borderWidth: 1.5,
          lineTension: 0,
          pointRadius: 0.5,
          borderColor: colors[colorNames[0]].base,
          backgroundColor: colors[colorNames[0]].base,
          label: this.$t('gateway.latency'),
          fill: false
        }]
      }
    },
    options() {
      return {
        legend: {
          display: this.shouldLegend
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: this.xAxisUnit,
              max: this.xAxisMaximum,
              min: this.xAxisMinimum
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              suggestedMax: this.unitMaximum
            },
            scaleLabel: {
              display: !!this.unitLabel,
              labelString: this.unitLabel
            }
          }]
        },
        tooltips: {
          intersect: false,
          mode: 'index',
          axis: 'x'
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        annotation: {
          drawTime: 'beforeDatasetsDraw',
          annotations: this.missingData.map(d => ({
            type: 'box',
            xScaleID: 'x-axis-0',
            xMin: d.from,
            xMax: d.to,
            backgroundColor: 'rgba(155, 0, 0, 0.1)',
            borderColor: 'rgba(0, 0, 0, 0.0)'
          }))
        }
      };
    },
    unitMaximum() {

    },
    unitLabel() {
      switch (this.type) {
        case 'memory':
          return 'mega bytes';
        case 'latency':
          return this.$t('gateway.milliseconds');
      }
    },
    typeName() {
      switch (this.type) {
        case 'load':
          return this.$t('gateway.load_average');
        case 'memory':
          return this.$t('gateway.free_memory');
        case 'latency':
          return this.$t('gateway.latency');
      }
    },
    shouldLegend() {
      switch (this.type) {
        case 'load':
          return true;
        case 'memory':
        case 'latency':
          return false;
      }
    },
    xAxisUnit() {
      return 'day';
    },
    xAxisMaximum() {
      return new Date().getTime();
    },
    xAxisMinimum() {
      return this.xAxisMaximum - 7 * 24 * 60 * 60 * 1000;
    }
  },
  methods: {
    load() {
      let ref = this.$db.ref('macHealth').child(this.mac).child(this.type);
      this.$bindAsObject('rawObject', ref, null, function() {});
    },
    refresh() {
      this.$db.ref('command/juice').push().set({
        juice: 'health',
        healthType: this.type,
        user: this.userId,
        mac: this.mac,
        type: this.generateJuiceType(this.type),
        days: 7
      });
    },
    generateJuiceType(type) {
      switch (type) {
        case 'load':
          return 'load_average';
        case 'memory':
          return 'memory';
        case 'latency':
          return 'latency';
      }
    }
  },
  mounted() {
    this.refresh();
    this.load();
  }
}
</script>



// WEBPACK FOOTER //
// src/components/gateway/GatewayChart.vue